import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useErrorBoundary } from 'react-error-boundary';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { visuallyHidden } from '@mui/utils';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Grid2 as Grid, Box, Typography, Stack, FormControl, Select, MenuItem, InputLabel, TextField, Button, InputAdornment, OutlinedInput, Paper, Divider, IconButton, Alert, CircularProgress, useTheme, useMediaQuery, TableContainer, Table, TableHead, TableRow, TableCell, TableFooter, TablePagination, TableSortLabel} from '@mui/material';
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { PictureAsPdf } from '@mui/icons-material';

import ReportItemRecordType from '../../../types/reports/reportItemRecordType';
import DropdownOption from '../../../types/dropdownOption';
import PageBreadcrumbs from '../../../components/PageBreadcrumbs';

import ShortFiler from '../../../types/filers/shortFiler';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import ContributionsTable from './ContributionsTable';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import ExpendituresTable from './ExpendituresTable';
import { ExpenditureSortOptions, ReceiptSortOptions } from '../../../types/referenceData/dataTableSortingOptions';
import DataGroupByTable from './DataGroupByTable';
import {DataGroupByItem} from './DataGroupByItem';

interface filter {
  name: string;
  description: string;
  deleteEvent: any;
}

const SeachReportsItems = (): JSX.Element => {

  
  document.title = 'OCPF - Search Report Items';

  const theme = useTheme();
  const { showBoundary } = useErrorBoundary();
  const [isLoading, setIsLoading] = useState(true);
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));

  //pass search from browser
  const queryParams = new URLSearchParams(window.location.search)

  const [itemsSummary, setItemsSummary] = useState('Loading...');

  const [receiptRows, setReceiptRows] = useState<ReceiptItem[]>([]);
  const [expenditureRows, setExpenditureRows] = useState<ExpenditureItem[]>([]);
  const [sortByOptions, setSortByOptions] = useState<DropdownOption[]>(ReceiptSortOptions);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [totalCount, setTotalCount] = useState(0);

  const [excelExportUrl, setExcelExportUrl] = useState('');
  const [txtExportUrl, setTxtExportUrl] = useState('');
  const [pdfExportUrl, setPdfExportUrl] = useState('');

  const isFirstLoad = useRef(true);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const filerInputRef = useRef<HTMLInputElement>(null);

  const [searchType, setSearchType] = useState(queryParams.get("schedule") || 'A');
  const [reportItemRecordTypes, setReportItemRecordTypes] = useState<ReportItemRecordType[]>();
  const [cityCodeList, setCityCodeList] = useState<DropdownOption[]>();
  const [placeholderText, setPlaceholderText] = useState("Search Contributor Name");
  const [nameDefaultValue, setNameDefaultValue] = useState(queryParams.get("name") || '');
  const [cpfIdDefaultValue, setCpfIdDefaultValue] = useState(queryParams.get("cpfId") || '');
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const defaultFilters = {
    searchTypeCategory: searchType,
    startDate: '',
    endDate: '',
    pagesize: 50,
    startIndex: 1,
    sortField: '',
    sortDirection: 'DESC',
    cpfId: cpfIdDefaultValue,
    recordTypeId: "-1",
    name: nameDefaultValue,
    cityCode: "-1",
    state: "",
    zipCode: "",
    occupation: "",
    employer: "",
    minAmount: "",
    maxAmount: "",
    description: "",
    withSummary: "true",

  }

  const [filters, setFilters] = useState(defaultFilters);

  const [filterCityCode, setFilterCityCode] = useState(filters.cityCode)
  const [filterState, setFilterState] = useState(filters.state)
  const [filterZipCode, setFilterZipCode] = useState(filters.zipCode)

  //group by section
  const [isGroupByDialogOpen, setIsGroupByDialogOpen] = useState(false);
  const [groupByRows, setGroupByRows] = useState<DataGroupByItem[]>([]);
  const [groupByFilter, setGroupByFilter] = useState("-1");
  const [groupbyDialogTitle, setGroupByDialogTitle] = useState("");

  const closeGroupByDialog = () => {
    setIsGroupByDialogOpen(false);
    setGroupByRows([]);
    setGroupByFilter("-1");
  }

  const updateGroupByFilter = async (event: any) => {
    let groupByField = event.target.value;
    setIsGroupByDialogOpen(true);
    setGroupByFilter(groupByField);
    let groupByTitle = searchType === "A" ? `${selectedFiler?.filerName || ""} Contributions: Group By ${groupByField}`: `${selectedFiler?.filerName || ""} Expenditures: Group By ${groupByField}`;
    setGroupByDialogTitle(groupByTitle);
    let params = [
      `groupSortField=Total`,
      `groupSortDirection=DESC`,
      `searchTypeCategory=${searchType}`,
      `cpfId=${filters.cpfId}`,
      `groupByField=${groupByField}`,
      `name=${filters.name}`,
      `startDate=${filters.startDate}`,
      `endDate=${filters.endDate}`,
      `cityCode=${filters.cityCode}`,
      `state=${filters.state}`,
      `zipCode=${filters.zipCode}`,
      `occupation=${filters.occupation}`,
      `employer=${filters.employer}`,
      `minAmount=${filters.minAmount}`,
      `maxAmount=${filters.maxAmount}`,
    ].join('&');

      try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/itemGroupings?${params}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the group by data.'));
          }
          return result.json();
        })
        .then(result => {
          setGroupByRows(result);
        });
      }catch(error){
          console.error('Uncaught Error:', error);
      }
  }


  //auto complete
  const [filerLabelText, setFilerLabelText] = useState("Find Filer Recipient");
  const [searchFilerOpen, setSearchFilerOpen] = useState(false);
  const [filerOptions, setFilerOptions] = useState<readonly ShortFiler[]>([]);
  const [selectedFiler, setSelectedFiler] = useState<ShortFiler | null>(null);

  const handleSearchFilerOpen = () => {
    setSearchFilerOpen(true);
  };

  const handleSearchFilerClose = (event: any) => {
    setSearchFilerOpen(false);
    setFilerOptions([]);
  };

  const searchFilerByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    (async () => {
      refreshFilerList(searchInput);
    })();
  }, 800);

  const updateSelectedFiler = (filer: ShortFiler) => {
    let filerCpfId = filer?.cpfId.toString() || "";
    let filterName = searchType === "A" ? "Recipient" : "Filer";
    let filter = { name: filterName, description: filer.display, deleteEvent: () => clearSelectedFiler() };
    setFilters({ ...filters, cpfId: filerCpfId });
    setSelectedFiler(filer);
  }

  const clearSelectedFiler = () => {
    setFilters({ ...filters, cpfId: "" });
    if (filerInputRef.current) {
      filerInputRef.current.value = "";
    }
  }

  const refreshFilerList = async (searchInput: string) => {
    try {
      if (searchInput) {
        let url = `${process.env.REACT_APP_API_BASE_URL}/filers/listings/A?searchPhrase=${searchInput}`;
        await fetch(url)
          .then(result => result.json())
          .then(result => {
            setFilerOptions(result.slice(0, 5));
          });
      }
      else {
        setFilerOptions([]);
      }

    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  useEffect(() => {
    if (isFirstLoad.current) {
      getReportItemRecordList();
      isFirstLoad.current = false;
      //handle query params
      if(queryParams.get("schedule") !== null) {
        let scheduleType = queryParams.get("schedule") ? queryParams.get("schedule") : filters.searchTypeCategory;
        setSearchType(scheduleType || "A");
        switch (scheduleType) {
          case "A":
            setSortByOptions(ReceiptSortOptions);
            setFilerLabelText("Find Filer Recipient");
            break;
          case "B":
            setSortByOptions(ExpenditureSortOptions);
            setFilerLabelText("Find Filer");
            break;
          case "S":
            setFilerLabelText("Find Filer");
            break;
        }
      }
    }
    else {
      getFilerReportItems();
    }
  }, [filters])

  const openFilters = (event: any) => {
    getCityCodeList();
    setIsMoreFiltersOpen(true);
  };

  const closeFilters = () => {
    setIsMoreFiltersOpen(false);
  };


  //pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters({ ...filters, startIndex: (newPage * pageSize + 1) });
  };

  //sorting
  const updateSortDirection = (event: any) => {
    let order = event.target.value;
    if(order){
      setOrder(order);
      setFilters({ ...filters, sortDirection: order });
    }
  }

  const updateSortField = (event: any) => {
    let sortField = event.target.value;
    if(event.target.value === "-1"){
      setOrderBy('date');
      sortField = 'date';
    }
    else{
      setOrderBy(event.target.value);
    }
    setFilters({ ...filters, sortField: sortField });
  }

  const updateSort = (sortField: string) => {
    let sortDirection = 'asc';
    if(sortField === orderBy){
      sortDirection = order === 'asc' ? 'desc' : 'asc';
    }
    setFilters({ ...filters, sortField: sortField, sortDirection: sortDirection });
    setOrderBy(sortField);
    setOrder(order === 'asc' ? 'desc' : 'asc');
  }

  const getReportItemRecordList = async () => {

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/recordTypes/${searchType}`)
        .then(result => result.json())
        .then(result => {
          setReportItemRecordTypes(result);
          getFilerReportItems();
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  const getCityCodeList = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}//municipalityList/options`)
        .then(result => result.json())
        .then(result => {
          let list = result.filter((item: DropdownOption) => item.value !== "");
          setCityCodeList(list);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  const getFilerReportItems = async () => {
    setItemsSummary("Loading data...");
    try {
      let params = new URLSearchParams({
        ...filters,
        //name: filters.name,
        //searchTypeCategory: filters.searchTypeCategory,
        pagesize: filters.pagesize.toString(),
        startIndex: filters.startIndex.toString(),
        withSummary: filters.withSummary.toString(),
      });

      setIsLoading(true);
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/items?${params.toString()}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get reports data.'));
          }
          return result.json();
        })
        .then(result => {
          let summary = result.summary;
          if (summary.count === 0) {
            setItemsSummary("No data found.");
            setTotalCount(0);
            setReceiptRows([]);
            setExpenditureRows([]);
          }
          else {
            let startCount = page * pageSize + 1;
            let endCount = summary.count < 50 ? summary.count : (page + 1) * pageSize;
            setItemsSummary(`Viewing ${startCount} through ${endCount} of ${summary.count} items, ${summary.totalDisplay}`);
            setTotalCount(summary.count);
            (filters.searchTypeCategory === 'A' || filters.searchTypeCategory === 'D') ? setReceiptRows(result.items) : setExpenditureRows(result.items);
          }

          setIsLoading(false);
          setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/excelOutput?${params.toString()}`);
          setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/textOutput?${params.toString()}`);
          setPdfExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/pdfOutput?${params.toString()}`);
        });

    } catch (error) {
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  const updateItemTypeFilter = (event: any) => {
    const itemType = event.target.value;
    setSearchType(itemType);
    setFilters({ ...filters, searchTypeCategory: event.target.value });
    switch (itemType) {
      case "D":
      case "A":
        setSortByOptions(ReceiptSortOptions);
        setPlaceholderText("Search Contributor Name");
        setFilerLabelText("Find Filer Recipient");
        break;
      case "B":
        setSortByOptions(ExpenditureSortOptions);
        setPlaceholderText("Search Vendor Name");
        setFilerLabelText("Find Filer");
        break;
      case "S":
        setSortByOptions(ExpenditureSortOptions);
        setPlaceholderText("Search Vendor Name");
        setFilerLabelText("Find Filer");
        break;
    }
  }

  const updateItemRecordTypeFilter = (event: any) => {
    setFilters({ ...filters, recordTypeId: event.target.value });
  }

  const updateSearchPhrase = debounce((event: any) => {
    let searchInput = event.target.value;
    setFilters({ ...filters, name: searchInput });
  }, 1000);

  const clearFilterSearchInput = () => {
    if (queryParams.get("schedule") !== null) {
      window.location.assign(window.location.pathname)
    }
    setFilters({ ...filters, name: "" });
    if (nameInputRef.current) {
      nameInputRef.current.value = "";
    }
  }

  //add a timeout for both typing and selecting the date
  const updateStartDate = debounce((date: Dayjs | null) => {
    if (dayjs(date, 'MM/DD/YYYY', true).isValid() || date === null) {
      setFilters({ ...filters, startDate: date?.toDate().toLocaleDateString() || '' });
    }
  }, 1000);


  const updateEndDate = debounce((date: Dayjs | null) => {
    if (dayjs(date, 'MM/DD/YYYY', true).isValid() || date === null) {
      setFilters({ ...filters, endDate: date?.toDate().toLocaleDateString() || '' });
    }
  }, 1000);

  const updateCityCodeFilter = (event: any) => {
    setFilterCityCode(event.target.value);
    setFilters({ ...filters, cityCode: event.target.value });
  }

  const updateStateFilter = debounce((event: any) => {
    setFilterState(event.target.value);
    setFilters({ ...filters, state: event.target.value.toUpperCase() });
  }, 1000)

  const updateZipFilter = debounce((event: any) => {
    setFilterZipCode(event.target.value);
    setFilters({ ...filters, zipCode: event.target.value });
  }, 1000)

  const updateOccupationFilter = debounce((event: any) => {
    setFilters({ ...filters, occupation: event.target.value });
  }, 1000)

  const updateEmployerFilter = debounce((event: any) => {
    setFilters({ ...filters, employer: event.target.value });
  }, 1000)

  const updateDescriptionFilter = debounce((event: any) => {
    setFilters({ ...filters, description: event.target.value });
  }, 1200)

  const updateMinAmountFilter = debounce((event: any) => {
    setFilters({ ...filters, minAmount: event.target.value });
  }, 1000)

  const updateMaxAmountFilter = debounce((event: any) => {
    setFilters({ ...filters, maxAmount: event.target.value });
  }, 1000)


  return (
    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
          variant="h5"
          align={'left'}
          sx={{
            fontWeight: 700,
          }}
          paddingTop={2}
          paddingBottom={2}
        >
          Search Contributions and Expenditures
        </Typography>
        <Divider />
        <Box sx={{ width: '100%' }} marginTop={3}>
          <Grid container marginBottom={2}>
            {/*item filter*/}
            <Grid size={12}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                {/*filters: search type & find filer*/}
                <Stack direction="row" spacing={0.5}>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel id="item-type-label">Type</InputLabel>
                    <Select
                      labelId="item-type-label"
                      id="item-type-select"
                      label="Type"
                      value={searchType}
                      onChange={updateItemTypeFilter}
                      aria-labelledby="item-type-label"
                    >
                      <MenuItem value={"A"}>Receipts</MenuItem>
                      <MenuItem value={"B"}>Expenditures</MenuItem>
                      <MenuItem value={"S"}>Subvendor Payments</MenuItem>
                      <MenuItem value={"D"}>Segregated Fund Donations</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 300 }}>
                    <Autocomplete
                      filterOptions={(x) => x}
                      size="small"
                      sx={{
                        width: 250,
                      }}
                      onChange={(event: any, newValue: ShortFiler | null) => {
                        if (newValue) {
                          updateSelectedFiler(newValue);
                        }
                        else {
                          clearSelectedFiler();
                        }
                      }}
                      open={searchFilerOpen}
                      onOpen={handleSearchFilerOpen}
                      onClose={handleSearchFilerClose}
                      isOptionEqualToValue={(option, value) => option.cpfId === value.cpfId}
                      getOptionLabel={(option) => option.filerName}
                      renderOption={(props, option) =>
                      (
                        <Stack
                          key={option.cpfId}
                          direction="row"
                          display={"flex"}
                          justifyContent={"flex-start"}>
                          <Stack>
                            <Typography variant='body2' {...props}>
                              {option.cpfId} {option.filerName}
                            </Typography>
                            {
                              option.isCandidate &&
                              <Typography variant='body2' component={"i"} color='text.secondary' paddingLeft={2}>
                                {option.officeSought}
                              </Typography>
                            }
                          </Stack>
                        </Stack>
                      )}
                      options={filerOptions}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant='outlined'
                          title='Search for a Filer'
                          inputRef={filerInputRef}
                          label={filerLabelText}
                          placeholder={"Type name or ID..."}
                          onChange={searchFilerByName}
                          slotProps={{
                            input: {
                              'aria-label': 'Search for a Filer',
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Stack>
                {/*filters: search by name */}
                <Stack direction="row" spacing={0.5}>
                  <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 250 }}>
                    <InputLabel htmlFor="search-name-input">{placeholderText}</InputLabel>
                    <OutlinedInput
                      defaultValue={nameDefaultValue}
                      id="search-name-input"
                      sx={{ paddingLeft: 0.5 }}
                      label={placeholderText}
                      placeholder={"Name or First Last"}
                      onChange={updateSearchPhrase}
                      aria-label="Search by name"
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </Stack>
                {/*filters: start date and end date */}
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker label="Start Date"
                        slotProps={
                          {
                            textField: { size: 'small' },
                            field: { clearable: true, onClear: () => updateStartDate(null) }
                          }
                        }
                        onChange={(newValue) => updateStartDate(newValue)}
                      />
                    </LocalizationProvider>
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker label="End Date"
                        slotProps={
                          {
                            textField: { size: 'small' },
                            field: { clearable: true, onClear: () => updateEndDate(null) }
                          }
                        }
                        onChange={(newValue) => updateEndDate(newValue)}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Stack>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                {/*filters: minimum and maximum */}
                <Stack direction="row" spacing={0.5} paddingTop={{ xs: 1, md: 0 }}>
                  <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 80 }}>
                    <InputLabel id='min-amount' htmlFor="min-amount-input">Minimum Amount</InputLabel>
                    <OutlinedInput
                      id="min-amount-input"
                      role='textbox'
                      aria-labelledby='min-amount'
                      label={"Minimum Amount"}
                      onChange={updateMinAmountFilter}
                    />
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 80 }}>
                    <InputLabel id='max-amount' htmlFor='max-amount-input'>Maximum Amount</InputLabel>
                    <OutlinedInput
                    id='max-amount-input'
                    role='textbox'
                      aria-labelledby='max-amount'
                      label={"Maximum Amount"}
                      onChange={updateMaxAmountFilter}
                    />
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  {
                    searchType === "B" &&
                    <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                      <InputLabel id="purpose" htmlFor='purpose-input'>Purpose</InputLabel>
                      <OutlinedInput
                      role='textbox'
                        aria-labelledby='purpose'
                        id='purpose-input'
                        label={"Search Purpose..."}
                        onChange={updateDescriptionFilter}
                      />
                    </FormControl>
                  }
                  {
                    searchType === "A" &&
                    <>
                      <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                        <InputLabel id="occupation" htmlFor='occupation-input'>Occupation</InputLabel>
                        <OutlinedInput
                          role='textbox'
                          id='occupation-input'
                          aria-labelledby='occupation'
                          label={"Occupation"}
                          onChange={updateOccupationFilter}
                        />
                      </FormControl>
                      <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                        <InputLabel id="employer" htmlFor='employer-input'>Employer</InputLabel>
                        <OutlinedInput
                          role='textbox'
                          id='employer-input'
                          aria-labelledby='employer'
                          label={"Employer"}
                          onChange={updateEmployerFilter}
                        />
                      </FormControl>
                    </>
                  }
                  {
                    !isMoreFiltersOpen &&
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openFilters}
                        sx={{ paddingTop: 0, paddingBottom: 0, maxWidth: 120 }}
                        startIcon={<FilterListIcon />}>
                        More filters
                      </Button>
                    </Stack>
                  }
                </Stack>
              </Stack>
            </Grid>
            {
              isMoreFiltersOpen &&
              <Grid size={{ xs: 12, md: 10 }}>
                <Stack direction={{ xs: "column", lg: "row" }} spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={0.5}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <Select
                        defaultValue={"-1"}
                        onChange={updateItemRecordTypeFilter}
                      >
                        <MenuItem value={"-1"}>All Record Types</MenuItem>
                        {
                          reportItemRecordTypes?.map((type) =>
                            <MenuItem value={type.value} key={type.value} >{type.text}</MenuItem>
                          )
                        }
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <Select
                        value={filterCityCode}
                        onChange={updateCityCodeFilter}
                      >
                        <MenuItem value={"-1"}>Choose a MA city or town</MenuItem>
                        {
                          cityCodeList?.map((city) =>
                            <MenuItem
                              value={city.value}
                              key={city.value}
                              sx={{ fontSize: 'smaller' }}
                            >
                              {city.text}
                            </MenuItem>
                          )
                        }
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 80 }}>
                      <InputLabel id="state" htmlFor='state-input'>State</InputLabel>
                      <OutlinedInput
                        id='state-input'
                        role='textbox'
                        label="State"
                        aria-labelledby='state'
                        onChange={updateStateFilter}
                        defaultValue={filterState}
                      />
                    </FormControl>
                    <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 100 }}>
                      <InputLabel id="zipcode" htmlFor='zipcode-input'>Zip Code</InputLabel>
                      <OutlinedInput
                        id='zipcode-input'
                        aria-labelledby='zipcode'
                        role='textbox'
                        label="Zip Code"
                        onChange={updateZipFilter}
                        defaultValue={filterZipCode}
                      />
                    </FormControl>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                      <Select
                        id="group-by-select"
                        value={groupByFilter}
                        onChange={updateGroupByFilter}
                        aria-labelledby="group-by-label"
                      >
                        <MenuItem value={"-1"}>Group By</MenuItem>
                        <MenuItem value={"city"}>City/Town</MenuItem>
                        <MenuItem value={"state"}>State</MenuItem>
                        <MenuItem value={"zip_code"}>Zip</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={closeFilters}
                      sx={{ paddingTop: 0, paddingBottom: 0, width: 120 }}
                      startIcon={<FilterAltOffIcon />}>
                      Fewer filters
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            }
            {/*sort and export*/}
            <Grid size={12}>
              <Stack direction={isSM?'column': 'row'} spacing={0.5} justifyContent={isSM ? 'flex-start' : 'space-between'} alignItems={'center'}>
                <Stack>
                  <Typography variant='body2' color='text.secondary' component={'i'}>
                    Contributions with an asterisk (*) are inkind contributions.
                  </Typography>
                </Stack>
                <Stack direction={"row"} spacing={0.5} alignItems={"center"} flexWrap={"wrap"}>
                  <Box>
                    <Button size="medium">Export:</Button>
                    <IconButton aria-label="export to excel" size="small" color="success" onClick={() => window.open(excelExportUrl)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z" /></svg>
                    </IconButton>
                    <IconButton aria-label="export to txt file" size="small" color="primary" onClick={() => window.open(txtExportUrl)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 16 16"><path fill="currentColor" fillRule="evenodd" d="M2 1.5a.5.5 0 0 1 .5-.5H9a.5.5 0 0 1 .354.146l2.5 2.5A.5.5 0 0 1 12 4v2h-1V5H8.5a.5.5 0 0 1-.5-.5V2H3v12h8v-.5h1v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5zm7 .707V4h1.793zM8.875 9.5c-.298.4-.593.803-.827 1.167a5.5 5.5 0 0 0-.374.664c-.091.2-.174.436-.174.669h1a.8.8 0 0 1 .084-.253q.102-.225.305-.54c.173-.267.383-.561.611-.87c.228.309.438.603.61.87q.203.315.306.54a1.3 1.3 0 0 1 .082.233l.002.015v.004l1 .001c0-.233-.083-.469-.174-.669a5.5 5.5 0 0 0-.374-.664a21 21 0 0 0-.827-1.167c.298-.4.593-.803.827-1.167c.147-.228.278-.454.374-.664c.091-.2.174-.435.174-.669h-1v.005l-.002.015l-.013.053a1.3 1.3 0 0 1-.069.18q-.102.225-.305.54a17 17 0 0 1-.611.87a17 17 0 0 1-.61-.87a4.5 4.5 0 0 1-.306-.54A.8.8 0 0 1 8.5 7h-1c0 .234.083.469.174.669c.096.21.227.436.374.664c.234.364.529.767.827 1.167M4 7h3v1H6v4H5V8H4zm8 0h3v1h-1v4h-1V8h-1z" clipRule="evenodd" /></svg>
                    </IconButton>
                    <IconButton aria-label="export to pdf" size="small" onClick={() => window.open(pdfExportUrl)}>
                      <PictureAsPdf />
                    </IconButton>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            <Grid size={12} marginTop={1} padding={0}>
              <Alert 
                icon={false} 
                severity="info"
                sx={{
                  '.MuiAlert-message': {
                    padding: '0px',
                    width: '100%',
                  }
                }}
                >
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={'center'}>
                    <Box width={{ xs: '100%', md: '60%' }} display={'flex'} justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems={'center'}>
                      <Typography variant='body2' fontWeight={600}>
                      {itemsSummary}
                      </Typography>
                    </Box>
                    <Box width={{ xs: '100%', md: '40%' }} display={'flex'} justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems={'center'}>
                      <TablePagination 
                        rowsPerPageOptions={[50]} 
                        count={totalCount}
                        rowsPerPage={50}
                        page={page}
                        onPageChange={handleChangePage}
                        showFirstButton
                        showLastButton
                        sx={
                          { 
                            "& .MuiTablePagination-displayedRows": { display: "none" },
                            "&.MuiTablePagination-root": { borderBottom: 0 },
                            "& .MuiTablePagination-toolbar": { minHeight: "24px" },
                          }
                        }
                      />
                    </Box>
                  </Stack>
              </Alert>
            </Grid>
          </Grid>
          {/*data table*/}
          <Grid size={{ xs: 12, md: 10 }} marginTop={1} marginBottom={1}>
            <TableContainer sx={{ width: '100%' }} component={Paper}>
              <Table aria-label="data table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                        <TableSortLabel
                          active={orderBy === 'date'}
                          direction={orderBy === 'date' ? order : 'desc'}
                          onClick={() => updateSort('date')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Date
                          </Typography>
                            {orderBy === 'date' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                    {
                      searchType === "S" &&
                      <TableCell>
                        <Typography variant={'subtitle2'} fontWeight={600}>
                          Sub-vendor
                        </Typography>
                      </TableCell>
                    }
                    <TableCell>
                      <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => updateSort('name')}
                        >
                           <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable'>
                            {searchType === "A" && "Contributor"}
                            {searchType === "B" && "Vendor"}
                            {searchType === "S" && "Vendor"}
                            {searchType === "D" && "Donor"}
                           </Typography>
                            {orderBy === 'name' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                    {
                      isSM &&
                      <TableCell align='right'>
                        <TableSortLabel
                          active={orderBy === 'amount'}
                          direction={orderBy === 'amount' ? order : 'asc'}
                          onClick={() => updateSort('amount')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Amount
                          </Typography>
                            {orderBy === 'amount' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                      </TableCell>
                    }
                    <TableCell>
                      <TableSortLabel
                          active={orderBy === 'city'}
                          direction={orderBy === 'city' ? order : 'asc'}
                          onClick={() => updateSort('city')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            City
                          </Typography>
                            {orderBy === 'city' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                          active={orderBy === 'state'}
                          direction={orderBy === 'state' ? order : 'asc'}
                          onClick={() => updateSort('state')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            State
                          </Typography>
                            {orderBy === 'state' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                          active={orderBy === 'zip_Code'}
                          direction={orderBy === 'zip_Code' ? order : 'asc'}
                          onClick={() => updateSort('zip_Code')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Zip
                          </Typography>
                            {orderBy === 'zip_Code' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      {
                        (searchType === "A" || searchType === "D") &&
                        <TableSortLabel
                          active={orderBy === 'employer'}
                          direction={orderBy === 'employer' ? order : 'asc'}
                          onClick={() => updateSort('employer')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Occupation/Employer
                          </Typography>
                            {orderBy === 'employer' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                      }
                      {
                        (searchType === "B" || searchType === "S") &&
                        <TableSortLabel
                          active={orderBy === 'description'}
                          direction={orderBy === 'description' ? order : 'asc'}
                          onClick={() => updateSort('description')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Purpose
                          </Typography>
                            {orderBy === 'description' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                      }
                    </TableCell>
                    {
                      !isSM &&
                      <TableCell align='right'>
                        <TableSortLabel
                          active={orderBy === 'amount'}
                          direction={orderBy === 'amount' ? order : 'asc'}
                          onClick={() => updateSort('amount')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            Amount
                          </Typography>
                            {orderBy === 'amount' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                      </TableCell>
                    }
                    <TableCell width={'20%'}>
                      <TableSortLabel
                          active={orderBy === 'Filer_Full_Name_Reverse'}
                          direction={orderBy === 'Filer_Full_Name_Reverse' ? order : 'asc'}
                          onClick={() => updateSort('Filer_Full_Name_Reverse')}
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            className='is-clickable' 
                          >
                            { (searchType === "A" || searchType === "D") ? "Recipient/Source" : "Filer/Source"}
                          </Typography>
                            {orderBy === 'Filer_Full_Name_Reverse' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                {
                  (searchType === "A" || searchType === "D") && <ContributionsTable tableRows={receiptRows} displayFiler={true}></ContributionsTable>
                }
                {
                  searchType === "B" && <ExpendituresTable tableRows={expenditureRows} displayFiler={true}></ExpendituresTable>
                }
                {
                  searchType === "S" && <ExpendituresTable tableRows={expenditureRows} displayFiler={true}></ExpendituresTable>
                }
                {
                  !isSM &&
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[50]}
                        count={totalCount}
                        rowsPerPage={50}
                        page={page}
                        showFirstButton
                        showLastButton
                        onPageChange={handleChangePage}
                      />
                    </TableRow>
                  </TableFooter>
                }
              </Table>
            </TableContainer>
            {
              isSM &&
              <Box marginTop={1}>
                <TablePagination
                  rowsPerPageOptions={[50]}
                  count={totalCount}
                  rowsPerPage={50}
                  page={page}
                  showFirstButton
                  showLastButton
                  onPageChange={handleChangePage}
                />
              </Box>
            }
      
          </Grid>

          {/* More filters */}
          {/*
                
                 <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              '.MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold !important'
              }
            }}>
            <DataGrid
              rows={tableRows}
              columns={tableColumns}
              getRowHeight={() => 'auto'}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              paginationMode="server"
              rowCount={totalCount}
              pageSizeOptions={[50]}
              columnVisibilityModel={{
                sourceDescription: false,
              }}
              sortModel={sortModel}
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              disableColumnMenu
              disableRowSelectionOnClick
              sx={{
                '.MuiDataGrid-cell': {
                  alignItems: 'center',
                  //whiteSpace: 'break-spaces!important',
                  wordBreak: 'break-word',
                  paddingTop: '3px!important',
                  paddingBottom: '3px!important',
                },
              }}
            />
          </Box>
                
                <Popover
                elevation={1}
                open={isMoreFiltersOpen}
                anchorEl={anchorEl}
                onClose={closeFilters}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
            <Paper sx={{ p: 2 }}>
                <Grid marginTop={1}>
                <Grid size={12}>
                  <Typography
                    variant={'subtitle2'}
                    marginBottom={0.5}
                    fontWeight={600}
                  >
                    Record Type
                  </Typography>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Select 
                      sx={{fontSize: 'smaller'}} 
                      defaultValue={"-1"}
                      onChange={updateItemRecordTypeFilter}
                      >
                      <MenuItem value={"-1"}>All Record Types</MenuItem>
                        {
                            reportItemRecordTypes?.map((type) =>
                            <MenuItem value={type.value} key={type.value} >{type.text}</MenuItem>
                        )
                        }
                  </Select>
                </FormControl>
                </Grid>
                 <Grid size={12}>
                    <Typography
                        variant={'subtitle2'}
                        marginBottom={0.5}
                        fontWeight={600}
                    >
                    From a Massachusetts City:
                    </Typography>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <Select 
                            sx={{fontSize: 'smaller'}} 
                            value={filterCityCode}
                            onChange={updateCityCodeFilter}
                        >
                        <MenuItem value={"-1"}>Choose a MA city or town</MenuItem>
                        {
                            cityCodeList?.map((city) =>
                            <MenuItem 
                                value={city.value} 
                                key={city.value}
                                sx={{fontSize: 'smaller'}}
                            >
                                {city.text}
                            </MenuItem>
                        )
                        }
                     </Select>
                    </FormControl>
                 </Grid>
                 <Grid size={12} marginTop={1}>
                    <Typography
                    variant={'subtitle2'}
                    marginBottom={1}
                    fontWeight={600}
                    >
                    From Another State:
                    </Typography>
                    <Stack direction={"row"} spacing={1}>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 80}}>
                            <InputLabel sx={{fontSize: "smaller"}}>State</InputLabel>
                            <OutlinedInput
                                label="State"
                                onChange={updateStateFilter}
                                sx={{ fontSize: 'smaller' }}
                                defaultValue={filterState}
                            />
                        </FormControl>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                            <InputLabel sx={{fontSize: "smaller"}}>Zip Code</InputLabel>
                            <OutlinedInput
                                label="Zip Code"
                                onChange={updateZipFilter}
                                sx={{ fontSize: 'smaller' }}
                                defaultValue={filterZipCode}
                            />
                        </FormControl>
                    </Stack>
                </Grid>
                {
                    searchType==="A" &&
                    <Grid size={12} marginTop={1}>
                        <Typography
                        variant={'subtitle2'}
                        marginBottom={1}
                        fontWeight={600}>
                        Contributor Info
                        </Typography>
                        <Stack direction={"row"} spacing={1}>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                            <InputLabel sx={{fontSize: "smaller"}}>Occupation</InputLabel>
                            <OutlinedInput
                                label={"Occupation"}
                                onChange={updateOccupationFilter}
                                sx={{ fontSize: 'smaller' }}
                                defaultValue={filterOccupation}
                            />
                        </FormControl>
                        <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                            <InputLabel sx={{fontSize: "smaller"}}>Employer</InputLabel>
                            <OutlinedInput
                                label={"Employer"}
                                onChange={updateEmployerFilter}
                                sx={{ fontSize: 'smaller' }}
                                defaultValue={filterEmployer}
                            />
                        </FormControl>
                        </Stack>
                    </Grid>
                }
                <Grid size={12} marginTop={2}>
                    <Divider />
                </Grid>
                <Grid container size={12}>
                <Stack
                direction={'row'}
                spacing={2}
                padding={2}
                sx={{
                    justifyContent: "flex-end",
                    alignItems: "center",
                }}
                >
                <Button 
                    size="small" 
                    onClick={closeFilters}
                    variant="text"
                    color="warning"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Close
                </Button>
                <Button 
                    size="small" 
                    onClick={closeFilters}
                    color="success"
                    variant="contained"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Apply
                </Button>
                <Button 
                    size="small" 
                    onClick={clearAllFilters}
                    color ="primary"
                    variant="contained"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Clear Filters
                </Button>
            </Stack>
          </Grid>
        </Grid>
        </Paper>
            </Popover>
            */}

        </Box>
        {/*Group by dialog*/}
        <DataGroupByTable isGroupByDialogOpen={isGroupByDialogOpen} closeGroupByDialog={closeGroupByDialog} rows={groupByRows} title={groupbyDialogTitle}></DataGroupByTable>
        {/*loading indicator*/}
        <CircularProgress
          aria-label='loading indicator'
          title='loading indicator'
          size={50}
          className={isLoading ? "" : "hidden"}
          sx={{
            position: "fixed",
            left: "50%",
            top: "20%",
            transform: "translate(-50%, -50%)",
            zIndex: 2
          }}
        />
      </Container>
    </Main>
  );
};

export default SeachReportsItems;